import React, { useState, useEffect } from 'react';
import { 
  CpuChipIcon, 
  WifiIcon, 
  ExclamationCircleIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ChartBarIcon,
  CogIcon,
  CubeIcon,
  BoltIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  PowerIcon,
  BugAntIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';
import { WifiIcon as WifiOffIcon } from '@heroicons/react/24/solid';

interface HardwareDevice {
  id: string;
  hardware_uuid: string;
  node_id: number;
  node_type: number;
  line_number: number;
  station_config: number;
  device_name: string;
  device_type: string;
  station_range: string;
  status: string;
  last_seen: string;
  first_seen: string;
  firmware_version: string;
  hardware_model: string;
  sparkplug_group: string;
  sparkplug_node: string;
  total_messages_sent: number;
  total_weight_processed: number;
  last_error_message: string;
  last_error_timestamp: string;
  connectivity_status: 'online' | 'stale' | 'offline';
  switch_values: {
    node_id: number;
    node_type: number;
    line_number: number;
    station_config: number;
  };
  is_simulated?: boolean;
  is_powered_on?: boolean;
  process_status?: string;
  description?: string;
  debug_mode?: boolean;
  last_reboot?: string;
  uptime_seconds?: number;
}

interface GroupedDevices {
  INFEED: HardwareDevice[];
  OUTFEED: HardwareDevice[];
  QC: HardwareDevice[];
  UNCONFIGURED: HardwareDevice[];
}

export default function HardwareRegistry() {
  const [devices, setDevices] = useState<HardwareDevice[]>([]);
  const [grouped, setGrouped] = useState<GroupedDevices>({
    INFEED: [],
    OUTFEED: [],
    QC: [],
    UNCONFIGURED: []
  });
  const [stats, setStats] = useState({
    total: 0,
    online: 0,
    stale: 0,
    offline: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<HardwareDevice | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<HardwareDevice | null>(null);
  const [formData, setFormData] = useState({
    node_id: 0,
    node_type: 0,
    line_number: 1,
    station_config: 1,
    description: '',
    enable_fault_injection: false,
    test_scenario: ''
  });
  const [controlStatus, setControlStatus] = useState<any>(null);
  const [loadingControl, setLoadingControl] = useState(false);

  const fetchHardwareRegistry = async () => {
    try {
      const response = await fetch('/api/hardware-registry');
      const result = await response.json();
      
      if (result.message === 'success') {
        setDevices(result.data.devices);
        setGrouped(result.data.grouped);
        setStats({
          total: result.data.total,
          online: result.data.online,
          stale: result.data.stale,
          offline: result.data.offline
        });
        setError(null);
      }
    } catch (err) {
      setError('Failed to fetch hardware registry');
      console.error('Error fetching hardware registry:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHardwareRegistry();
    
    if (autoRefresh) {
      const interval = setInterval(fetchHardwareRegistry, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);
  
  // Fetch control status when a device is selected
  useEffect(() => {
    if (selectedDevice) {
      fetchControlStatus(selectedDevice.hardware_uuid);
    } else {
      setControlStatus(null);
    }
  }, [selectedDevice]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <WifiIcon className="h-4 w-4 text-green-500" />;
      case 'stale':
        return <ExclamationCircleIcon className="h-4 w-4 text-yellow-500" />;
      case 'offline':
        return <WifiOffIcon className="h-4 w-4 text-red-500" />;
      default:
        return <ExclamationCircleIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getDeviceTypeIcon = (type: string) => {
    switch (type) {
      case 'INFEED':
        return <CubeIcon className="h-5 w-5 text-blue-500" />;
      case 'OUTFEED':
        return <ChartBarIcon className="h-5 w-5 text-green-500" />;
      case 'QC':
        return <BoltIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <CogIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const formatSwitchValue = (value: number, type: 'id' | 'type' | 'line' | 'config', nodeType?: number) => {
    if (value === 0) return '0 (Unconfigured)';
    
    switch (type) {
      case 'type':
        switch (value) {
          case 1: return '1 (Infeed)';
          case 2: return '2 (Outfeed)';
          case 3: return '3 (QC)';
          default: return value.toString();
        }
      case 'config':
        if (nodeType === 1) {
          // Infeed hopper number
          return `${value} (Hopper ${value})`;
        } else if (nodeType === 2) {
          // Outfeed station groups
          const ranges = [
            '', '1-4', '5-8', '9-12', '13-16', '17-20',
            '21-24', '25-28', '29-32', '33-36'
          ];
          return `${value} (Stations ${ranges[value] || 'Unknown'})`;
        }
        return value.toString();
      default:
        return value.toString();
    }
  };

  const handlePowerToggle = async (device: HardwareDevice) => {
    if (!device.is_simulated) return;
    
    try {
      const endpoint = device.is_powered_on ? 'power-off' : 'power-on';
      const response = await fetch(`/api/simulated-hardware/${device.hardware_uuid}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        await fetchHardwareRegistry();
      }
    } catch (err) {
      console.error('Error toggling power:', err);
    }
  };
  
  const handleDeleteDevice = async (device: HardwareDevice) => {
    if (!device.is_simulated || !confirm(`Delete simulated device "${device.device_name}"?`)) return;
    
    try {
      const response = await fetch(`/api/simulated-hardware/${device.hardware_uuid}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await fetchHardwareRegistry();
        setSelectedDevice(null);
      }
    } catch (err) {
      console.error('Error deleting device:', err);
    }
  };
  
  const handleSaveDevice = async () => {
    try {
      const url = editingDevice 
        ? `/api/simulated-hardware/${editingDevice.hardware_uuid}`
        : '/api/simulated-hardware';
      
      const response = await fetch(url, {
        method: editingDevice ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        await fetchHardwareRegistry();
        setShowAddModal(false);
        setEditingDevice(null);
        setFormData({
          node_id: 0,
          node_type: 0,
          line_number: 1,
          station_config: 1,
          description: '',
          enable_fault_injection: false,
          test_scenario: ''
        });
      }
    } catch (err) {
      console.error('Error saving device:', err);
    }
  };
  
  const fetchControlStatus = async (hardwareUuid: string) => {
    try {
      const response = await fetch(`/api/hardware/${hardwareUuid}/control-status`);
      if (response.ok) {
        const result = await response.json();
        setControlStatus(result.data);
      }
    } catch (err) {
      console.error('Error fetching control status:', err);
    }
  };
  
  const handleReboot = async (device: HardwareDevice) => {
    if (!confirm(`Reboot hardware device "${device.device_name}"? The device will be temporarily unavailable.`)) {
      return;
    }
    
    setLoadingControl(true);
    try {
      const response = await fetch(`/api/hardware/${device.hardware_uuid}/reboot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Reboot command sent to ${device.device_name}`);
        await fetchControlStatus(device.hardware_uuid);
      } else {
        const error = await response.json();
        alert(`Failed to send reboot command: ${error.error}`);
      }
    } catch (err) {
      console.error('Error sending reboot command:', err);
      alert('Failed to send reboot command');
    } finally {
      setLoadingControl(false);
    }
  };
  
  const handleDebugModeToggle = async (device: HardwareDevice) => {
    const newDebugMode = !controlStatus?.debug_mode;
    const action = newDebugMode ? 'enable' : 'disable';
    
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} debug mode for "${device.device_name}"?`)) {
      return;
    }
    
    setLoadingControl(true);
    try {
      const response = await fetch(`/api/hardware/${device.hardware_uuid}/debug-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ debug_mode: newDebugMode })
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Debug mode ${newDebugMode ? 'enabled' : 'disabled'} for ${device.device_name}`);
        await fetchControlStatus(device.hardware_uuid);
      } else {
        const error = await response.json();
        alert(`Failed to ${action} debug mode: ${error.error}`);
      }
    } catch (err) {
      console.error(`Error ${action}ing debug mode:`, err);
      alert(`Failed to ${action} debug mode`);
    } finally {
      setLoadingControl(false);
    }
  };
  
  const DeviceCard = (props: any) => {
    const { device } = props;
    return (
      <div 
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
      >
      <div 
        className="cursor-pointer"
        onClick={() => setSelectedDevice(device)}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            {getDeviceTypeIcon(device.device_type)}
            <div>
              <h3 className="font-semibold text-gray-900">{device.device_name}</h3>
              {device.is_simulated && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">SIMULATED</span>
              )}
            </div>
          </div>
          {getStatusIcon(device.connectivity_status)}
        </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">UUID:</span>
          <span className="font-mono text-xs">{device.hardware_uuid.slice(0, 8)}...</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-500">Firmware:</span>
          <span>{device.firmware_version}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-500">Last Seen:</span>
          <span>{formatTimestamp(device.last_seen)}</span>
        </div>
        
        {device.last_reboot && (
          <div className="flex justify-between">
            <span className="text-gray-500">Last Reboot:</span>
            <span className="text-amber-600">{formatTimestamp(device.last_reboot)}</span>
          </div>
        )}
        
        <div className="grid grid-cols-4 gap-1 mt-3 pt-3 border-t border-gray-100">
          <div className="text-center">
            <div className="text-xs text-gray-400">SW1</div>
            <div className="font-mono text-sm">{device.switch_values.node_id}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">SW2</div>
            <div className="font-mono text-sm">{device.switch_values.node_type}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">SW3</div>
            <div className="font-mono text-sm">{device.switch_values.line_number}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">SW4</div>
            <div className="font-mono text-sm">{device.switch_values.station_config}</div>
          </div>
        </div>
      </div>
      </div>
      </div>
    );
  };

  const DeviceSection = (props: any) => {
    const { title, devices, icon } = props;
    if (devices.length === 0) return null;
    
    return (
      <div className="mb-8">
        <div className="flex items-center space-x-2 mb-4">
          {icon}
          <h2 className="text-lg font-semibold text-gray-900">
            {title} ({devices.length})
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {devices.map((device, index) => (
            <DeviceCard key={`${device.hardware_uuid}-${device.is_simulated ? 'sim' : 'real'}-${index}`} device={device} />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <CpuChipIcon className="h-6 w-6" />
              <span>Hardware Registry</span>
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Registered hardware devices on the network
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Auto-refresh</span>
            </label>
            
            <button
              onClick={fetchHardwareRegistry}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center space-x-2"
            >
              <ArrowPathIcon className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-500">Total Devices</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-600">{stats.online}</div>
          <div className="text-sm text-green-700">Online</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="text-2xl font-bold text-yellow-600">{stats.stale}</div>
          <div className="text-sm text-yellow-700">Stale (30s-5min)</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="text-2xl font-bold text-red-600">{stats.offline}</div>
          <div className="text-sm text-red-700">Offline</div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Device Sections */}
      <DeviceSection 
        title="Infeed Hardware" 
        devices={grouped.INFEED} 
        icon={<CubeIcon className="h-5 w-5 text-blue-500" />}
      />
      
      <DeviceSection 
        title="Outfeed Hardware" 
        devices={grouped.OUTFEED} 
        icon={<ChartBarIcon className="h-5 w-5 text-green-500" />}
      />
      
      <DeviceSection 
        title="QC Hardware" 
        devices={grouped.QC} 
        icon={<BoltIcon className="h-5 w-5 text-purple-500" />}
      />
      
      <DeviceSection 
        title="Unconfigured Hardware" 
        devices={grouped.UNCONFIGURED} 
        icon={<CogIcon className="h-5 w-5 text-gray-400" />}
      />

      {/* Device Details Modal */}
      {selectedDevice && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedDevice(null)}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                {getDeviceTypeIcon(selectedDevice.device_type)}
                <span>{selectedDevice.device_name}</span>
              </h2>
              {getStatusIcon(selectedDevice.connectivity_status)}
            </div>
            
            <div className="space-y-4">
              {/* Basic Information */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Basic Information</h3>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <dt className="text-gray-500">Hardware UUID:</dt>
                    <dd className="font-mono">{selectedDevice.hardware_uuid}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Device Type:</dt>
                    <dd>{selectedDevice.device_type}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Firmware Version:</dt>
                    <dd>{selectedDevice.firmware_version}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Hardware Model:</dt>
                    <dd>{selectedDevice.hardware_model}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Station Range:</dt>
                    <dd>{selectedDevice.station_range || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Line Number:</dt>
                    <dd>{selectedDevice.line_number || 'Unconfigured'}</dd>
                  </div>
                </dl>
              </div>
              
              {/* Node Switch Settings */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Node Switch Settings</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-xs text-gray-500 mb-1">Switch 1 - ID</div>
                    <div className="font-mono text-lg">{formatSwitchValue(selectedDevice.switch_values.node_id, 'id')}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-xs text-gray-500 mb-1">Switch 2 - Type</div>
                    <div className="font-mono text-lg">{formatSwitchValue(selectedDevice.switch_values.node_type, 'type')}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-xs text-gray-500 mb-1">Switch 3 - Line</div>
                    <div className="font-mono text-lg">{formatSwitchValue(selectedDevice.switch_values.line_number, 'line')}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-xs text-gray-500 mb-1">Switch 4 - Config</div>
                    <div className="font-mono text-lg">
                      {formatSwitchValue(selectedDevice.switch_values.station_config, 'config', selectedDevice.switch_values.node_type)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* MQTT Information */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">MQTT Information</h3>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <dt className="text-gray-500">Sparkplug Group:</dt>
                    <dd className="font-mono">{selectedDevice.sparkplug_group}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Sparkplug Node:</dt>
                    <dd className="font-mono">{selectedDevice.sparkplug_node}</dd>
                  </div>
                </dl>
              </div>
              
              {/* Statistics */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Statistics</h3>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <dt className="text-gray-500">Messages Sent:</dt>
                    <dd>{(Number(selectedDevice.total_messages_sent) || 0).toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Weight Processed:</dt>
                    <dd>{(Number(selectedDevice.total_weight_processed) || 0).toFixed(2)} lbs</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">First Seen:</dt>
                    <dd>{selectedDevice.first_seen ? new Date(selectedDevice.first_seen).toLocaleString() : 'Never'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Last Seen:</dt>
                    <dd>{selectedDevice.last_seen ? new Date(selectedDevice.last_seen).toLocaleString() : 'Never'}</dd>
                  </div>
                </dl>
              </div>
              
              {/* Error Information */}
              {selectedDevice.last_error_message && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Last Error</h3>
                  <div className="bg-red-50 p-3 rounded text-sm">
                    <div className="text-red-700">{selectedDevice.last_error_message}</div>
                    {selectedDevice.last_error_timestamp && (
                      <div className="text-red-500 text-xs mt-1">
                        {new Date(selectedDevice.last_error_timestamp).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Hardware Control */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Hardware Control</h3>
                <div className="space-y-3">
                  {/* Debug Mode Status and Toggle */}
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <div className="flex items-center space-x-2">
                      <BugAntIcon className="h-5 w-5 text-gray-600" />
                      <div>
                        <div className="font-medium">Debug Mode</div>
                        <div className="text-sm text-gray-500">
                          {controlStatus?.debug_mode ? 'Enabled' : 'Disabled'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDebugModeToggle(selectedDevice)}
                      disabled={loadingControl}
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        controlStatus?.debug_mode
                          ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                          : 'bg-gray-600 text-white hover:bg-gray-700'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {loadingControl ? 'Processing...' : (controlStatus?.debug_mode ? 'Disable Debug' : 'Enable Debug')}
                    </button>
                  </div>
                  
                  {/* Reboot Button */}
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <div className="flex items-center space-x-2">
                      <ComputerDesktopIcon className="h-5 w-5 text-gray-600" />
                      <div>
                        <div className="font-medium">Device Reboot</div>
                        <div className="text-sm text-gray-500">
                          {controlStatus?.last_reboot 
                            ? `Last reboot: ${formatTimestamp(controlStatus.last_reboot)}`
                            : 'No recent reboot'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleReboot(selectedDevice)}
                      disabled={loadingControl || selectedDevice.connectivity_status === 'offline'}
                      className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingControl ? 'Processing...' : 'Reboot Device'}
                    </button>
                  </div>
                  
                  {/* Uptime Information */}
                  {controlStatus?.uptime_seconds !== undefined && (
                    <div className="bg-blue-50 p-3 rounded text-sm">
                      <div className="text-blue-700">
                        Uptime: {Math.floor(controlStatus.uptime_seconds / 3600)}h {Math.floor((controlStatus.uptime_seconds % 3600) / 60)}m
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedDevice(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}